import { getIndexName, search } from '@src/clients/Algolia';
import { AlgoliaFacetsType } from '@src/clients/Algolia/constants';
import { GetMemberPlanQuery } from '@src/graphqlTypes/catalogServiceTypes';
import { datadogRum } from '@datadog/browser-rum';
import { SearchActionTypes } from '@src/reducers/searchReducer';
import { toast } from 'react-toastify';
import { logDataDogError } from '@src/utilities/datadog';
import { OptimizelyDecision } from '@optimizely/react-sdk';

export const HITS_PER_PAGE = 10;

export const onStateChange = ({ uiState, setUiState, index }) => {
	const newUiState = {
		[index]: {
			...uiState[index],
			menu: {
				completionTimeRange: uiState[index].menu?.completionTimeRange,
				weeklyEffortRange: uiState[index].menu?.weeklyEffortRange,
			},
			refinementList: {
				fundingLevel: uiState[index].refinementList?.fundingLevel,
				'genericRoles.lvl0': uiState[index].refinementList?.['genericRoles.lvl0'],
				'genericRoles.lvl1': uiState[index].refinementList?.['genericRoles.lvl1'],
				'destinationRoles.lvl0': uiState[index].refinementList?.['destinationRoles.lvl0'],
				'destinationRoles.lvl1': uiState[index].refinementList?.['destinationRoles.lvl1'],
				'hierarchicalInterests.lvl0': uiState[index].refinementList?.['hierarchicalInterests.lvl0'],
				'hierarchicalInterests.lvl1': uiState[index].refinementList?.['hierarchicalInterests.lvl1'],
				programFormatDisplay: uiState[index].refinementList?.programFormatDisplay,
				'programType.lvl0': uiState[index].refinementList?.['programType.lvl0'],
				'programType.lvl1': uiState[index].refinementList?.['programType.lvl1'],
				'specificRoles.lvl0': uiState[index].refinementList?.['specificRoles.lvl0'],
				'specificRoles.lvl1': uiState[index].refinementList?.['specificRoles.lvl1'],
				'studentFacingAcademicPartner.name':
					uiState[index].refinementList?.['studentFacingAcademicPartner.name'],
			},
			page: uiState[index].page,
			query: uiState[index].query || '',
		},
	};

	setUiState(newUiState);
	return newUiState;
};

export const toggleMemberServicesToast = (memberUuid: string) => {
	const toastId = 'memberServicesToast';
	toast.dismiss(toastId);

	toast(`You are viewing member: ${memberUuid}'s plan catalog.`, {
		toastId,
		autoClose: false,
		position: 'bottom-center',
		draggable: false,
		closeOnClick: false,
		type: 'info',
		onClose: () => null,
		className: 'switcher-toast',
	});
};

export const updateSearchState = async (
	decision: OptimizelyDecision,
	dispatch: React.Dispatch<SearchActionTypes>,
	response: GetMemberPlanQuery,
	isMemberServicesRoute: boolean,
	memberUuid: string,
	employerUuid: string
) => {
	const index = getIndexName(decision);
	const planUuid = response.memberPlan.planUuid;
	let params;

	try {
		const filters = `visibleTo:${planUuid} AND show:true`;
		params = { filters, facets: ['*'], page: 0 };
		const algoliaResponse = await search('', params, index);
		const hasFacets = Object.keys(algoliaResponse.facets).length > 0;

		if (hasFacets) {
			if (isMemberServicesRoute) {
				toggleMemberServicesToast(memberUuid);
			}

			dispatch({
				type: 'set_search_state',
				filters,
				employerUuid,
				facets: algoliaResponse.facets as unknown as AlgoliaFacetsType,
				index,
				indexAvailable: true,
				isMemberServicesRoute,
				memberPlanData: response.memberPlan || null,
				memberUuid,
				planUuid,
				staticTotalPrograms: algoliaResponse.nbHits,
			});
		} else {
			dispatch({
				type: 'set_no_facets_returned',
				facets: {} as AlgoliaFacetsType,
				hasFacets: false,
				indexAvailable: false,
			});
			datadogRum.addAction('NoFacetsReturned', {
				params,
				planUuid,
			});
		}
	} catch (error) {
		datadogRum.addAction('GetFacetsError', {
			error: error.message,
			params,
			planUuid,
		});
		logDataDogError(error, 'AlgoliaError', {
			error,
			params,
			planUuid,
		});

		dispatch({
			type: 'set_search_state_error',
			facets: {} as AlgoliaFacetsType,
			indexAvailable: false,
			searchError: error,
		});
	}
};

interface UpdateNoProgramsSearchStateProps {
	dispatch: React.Dispatch<SearchActionTypes>;
	employerUuid: string;
	isMemberServicesRoute: boolean;
	memberUuid: string;
	planUuid: string;
	response: GetMemberPlanQuery;
}

export const updateNoProgramsSearchState = ({
	dispatch,
	employerUuid,
	isMemberServicesRoute,
	memberUuid,
	planUuid,
	response,
}: UpdateNoProgramsSearchStateProps) => {
	if (isMemberServicesRoute) {
		toggleMemberServicesToast(memberUuid);
	}
	dispatch({
		type: 'set_search_state',
		employerUuid,
		isMemberServicesRoute,
		memberPlanData: response.memberPlan || null,
		memberUuid,
		planUuid,
	});
};
